import { addDays, addMonths, endOfMonth, startOfMonth, subMonths } from "date-fns";

import {
  createFacilityShiftCountsQueryOptions,
  type FacilityShiftCountsResponse,
} from "../api/useGetFacilityShiftCounts";
import { type ShiftCardData } from "../types";
import { type PendingInviteInfo } from "../utils/mapShiftToCard";
import { mergeDayCounts } from "../utils/mergeDayCounts";
import { groupShiftsByTimeSlot } from "../utils/shiftGroupingHelpers";

export function getSelectedAgentRequestsArray(
  selectedAgentReqs: Set<string> | undefined
): string[] {
  if (!selectedAgentReqs || selectedAgentReqs.size === 0) {
    return [];
  }

  const array: string[] = [];
  selectedAgentReqs.forEach((item) => {
    array.push(item);
  });

  return array.sort((a, b) => a.localeCompare(b));
}

export interface BuildShiftCountQueriesParams {
  selectedAgentRequestsArray: string[];
  facilityId: string;
  dateRange: { start: string; end: string };
  timezone: string;
}

export function buildShiftCountQueries(params: BuildShiftCountQueriesParams) {
  const { selectedAgentRequestsArray, facilityId, dateRange, timezone } = params;
  const enabled = Boolean(facilityId) && Boolean(timezone);

  if (selectedAgentRequestsArray.length > 0) {
    return selectedAgentRequestsArray.map((agentRequest) =>
      createFacilityShiftCountsQueryOptions({
        facilityId,
        dateFilter: dateRange,
        timezone,
        agentReq: agentRequest,
        enabled,
      })
    );
  }

  return [
    createFacilityShiftCountsQueryOptions({
      facilityId,
      dateFilter: dateRange,
      timezone,
      agentReq: undefined,
      enabled,
    }),
  ];
}

export interface ShiftCountQueryResult {
  data?: FacilityShiftCountsResponse;
  isLoading: boolean;
  isError: boolean;
}

export function combineShiftCountResults(queries: ShiftCountQueryResult[]) {
  const isLoadingCounts = queries.some((query) => query.isLoading);
  const isErrorCounts = queries.some((query) => query.isError);

  const combinedShiftCounts: FacilityShiftCountsResponse = {};

  for (const query of queries) {
    if (!query.data) {
      continue;
    }

    for (const [dateKey, dayCounts] of Object.entries(query.data)) {
      const existingCounts = combinedShiftCounts[dateKey];

      combinedShiftCounts[dateKey] = existingCounts
        ? mergeDayCounts(existingCounts, dayCounts)
        : { ...dayCounts };
    }
  }

  return { combinedShiftCounts, isLoadingCounts, isErrorCounts };
}

export function filterShiftsByAgentRequests(
  shifts: ShiftCardData[],
  selectedAgentReqs: Set<string> | undefined
): ShiftCardData[] {
  if (!selectedAgentReqs || selectedAgentReqs.size === 0) {
    return shifts;
  }

  return shifts.filter((shift) => selectedAgentReqs.has(shift.postedQualificationName));
}

export function createInitialDateRange(selectedDate: Date): { start: string; end: string } {
  const previousMonth = startOfMonth(subMonths(selectedDate, 1));
  const nextMonth = endOfMonth(addMonths(selectedDate, 1));
  return {
    start: previousMonth.toISOString(),
    end: nextMonth.toISOString(),
  };
}

export function computeNextDayAmShifts(
  selectedDate: Date,
  getCachedDayShifts: (date: Date) => ShiftCardData[] | undefined,
  selectedAgentReqs: Set<string> | undefined
): ShiftCardData[] {
  const nextDay = addDays(selectedDate, 1);
  const cachedShifts = getCachedDayShifts(nextDay);
  if (!cachedShifts) {
    return [];
  }

  const filteredShifts = filterShiftsByAgentRequests(cachedShifts, selectedAgentReqs);
  return groupShiftsByTimeSlot(filteredShifts).am;
}

export function filterExpiredInvitesByAgentRequests(
  expiredInvites: PendingInviteInfo[],
  selectedAgentReqs: Set<string> | undefined
): PendingInviteInfo[] {
  if (!selectedAgentReqs || selectedAgentReqs.size === 0) {
    return expiredInvites;
  }

  return expiredInvites.filter((invite) => selectedAgentReqs.has(invite.shiftDetails.agentReq));
}

export interface GroupedPendingInvites {
  am: PendingInviteInfo[];
  pm: PendingInviteInfo[];
  noc: PendingInviteInfo[];
  /**
   * Always empty — invites are not offered for orientation shifts. Kept on the
   * type so consumers can iterate the 4-slot model uniformly.
   */
  ori: PendingInviteInfo[];
}

export function groupPendingInvitesByTimeSlot(invites: PendingInviteInfo[]): GroupedPendingInvites {
  return invites.reduce<GroupedPendingInvites>(
    (groups, invite) => {
      const timeSlot = invite.shiftDetails.timeSlot as keyof GroupedPendingInvites;
      if (timeSlot === "ori") {
        return groups;
      }
      return {
        ...groups,
        [timeSlot]: [...(groups[timeSlot] ?? []), invite],
      };
    },
    { am: [], pm: [], noc: [], ori: [] }
  );
}
